import React, { useEffect, useMemo, useState } from 'react';
import { Marker, Polygon } from '@react-google-maps/api';
import { format } from 'date-fns';
import GoogleMapProvider from '../../components/map/GoogleMapProvider';
import BaseMap from '../../components/map/BaseMap';
import { useProject } from '../../contexts/ProjectContext';
import { seekFindService } from '../../services/seekFind.service';
import { mapToolsService } from '../../services/mapTools.service';
import { MapArea } from '../../types/maps.types';
import { DiscoveredLocation, SEEK_OUTCOMES, SeekOutcome, seekOutcomeMeta } from '../../types/seekFind.types';
import { MapPin, Loader2 } from 'lucide-react';

const SeekFindDataInner: React.FC = () => {
  const { selectedProjectId, selectedProject } = useProject();
  const [locations, setLocations] = useState<DiscoveredLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState<SeekOutcome | 'all'>('all');
  const [canvasserFilter, setCanvasserFilter] = useState<string>('all');
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  // Saved Maps-tool areas (e.g. the district boundary) — overlaid so the
  // outreach universe reads in relation to the work area. On by default.
  const [areas, setAreas] = useState<MapArea[]>([]);
  const [showAreas, setShowAreas] = useState(true);

  useEffect(() => {
    if (!selectedProjectId) { setAreas([]); return; }
    mapToolsService.getAreas(selectedProjectId).then(setAreas).catch(() => {});
  }, [selectedProjectId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      if (!selectedProjectId) { setLocations([]); setLoading(false); return; }
      const data = await seekFindService.getByProject(selectedProjectId);
      if (active) { setLocations(data); setLoading(false); }
    })();
    return () => { active = false; };
  }, [selectedProjectId]);

  const canvassers = useMemo(() => {
    const m = new Map<string, string>();
    locations.forEach(l => m.set(l.capturedBy, l.capturedByName || l.capturedBy));
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [locations]);

  const filtered = useMemo(() => locations.filter(l =>
    (outcomeFilter === 'all' || l.outcome === outcomeFilter) &&
    (canvasserFilter === 'all' || l.capturedBy === canvasserFilter)
  ), [locations, outcomeFilter, canvasserFilter]);

  const counts = useMemo(() => ({
    total: locations.length,
    no_answer: locations.filter(l => l.outcome === 'no_answer').length,
    attempt: locations.filter(l => l.outcome === 'attempt').length,
    survey: locations.filter(l => l.outcome === 'survey').length,
    success: locations.filter(l => l.outcome === 'success').length,
  }), [locations]);

  // Fit map to the filtered points.
  useEffect(() => {
    if (!mapRef || filtered.length === 0 || !(window as any).google) return;
    const b = new google.maps.LatLngBounds();
    filtered.forEach(l => b.extend(l.location));
    mapRef.fitBounds(b);
  }, [mapRef, filtered]);

  const dotIcon = (color: string) => ((window as any).google ? {
    path: google.maps.SymbolPath.CIRCLE, fillColor: color, fillOpacity: 0.9,
    strokeColor: '#fff', strokeWeight: 1.5, scale: 6,
  } : undefined);

  if (!selectedProjectId) return <div className="p-6 text-gray-500">Select a project to view seek &amp; find data.</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Seek &amp; Find — Data</h1>
        <p className="text-gray-600">{selectedProject?.name} · accumulated field outreach universe</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase">Total</p>
          <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
        </div>
        {SEEK_OUTCOMES.map(o => (
          <div key={o.value} className="bg-white rounded-lg shadow p-4">
            <p className="text-xs uppercase" style={{ color: o.color }}>{o.label}</p>
            <p className="text-2xl font-bold text-gray-900">{(counts as any)[o.value]}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          <button onClick={() => setOutcomeFilter('all')} className={`px-3 py-1.5 rounded-md text-sm ${outcomeFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>All</button>
          {SEEK_OUTCOMES.map(o => (
            <button key={o.value} onClick={() => setOutcomeFilter(o.value)} className={`px-3 py-1.5 rounded-md text-sm ${outcomeFilter === o.value ? 'text-white' : 'bg-gray-100 text-gray-700'}`} style={outcomeFilter === o.value ? { backgroundColor: o.color } : {}}>{o.label}</button>
          ))}
        </div>
        <select value={canvasserFilter} onChange={e => setCanvasserFilter(e.target.value)} className="px-3 py-1.5 border rounded-md text-sm">
          <option value="all">All canvassers</option>
          {canvassers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="text-sm text-gray-500">{filtered.length} shown</span>
        {areas.length > 0 && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer ml-auto">
            <input type="checkbox" checked={showAreas} onChange={(e) => setShowAreas(e.target.checked)} className="h-4 w-4 text-indigo-600 rounded" />
            Show saved areas ({areas.length})
          </label>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : locations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <MapPin className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          No seek &amp; find submissions yet for this project.
        </div>
      ) : (
        <>
          {/* Map */}
          <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: 360 }}>
            <BaseMap center={filtered[0]?.location || { lat: 39.8283, lng: -98.5795 }} zoom={14} onMapLoad={setMapRef}>
              {showAreas && areas.map(a => (
                <Polygon key={a.id} paths={a.paths} options={{ fillColor: a.color || '#4F46E5', fillOpacity: 0.08, strokeColor: a.color || '#4F46E5', strokeWeight: 2, clickable: false }} />
              ))}
              {filtered.map(l => (
                <Marker key={l.id} position={l.location} icon={dotIcon(seekOutcomeMeta(l.outcome).color)} title={`${seekOutcomeMeta(l.outcome).label} · ${l.capturedByName || ''}`} />
              ))}
            </BaseMap>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Outcome</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Canvasser</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered
                  .slice()
                  .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))
                  .map(l => {
                    const meta = seekOutcomeMeta(l.outcome);
                    return (
                      <tr key={l.id}>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                          {l.createdAt?.toDate ? format(l.createdAt.toDate(), 'MMM d, h:mm a') : '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: meta.color }}>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />{meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{l.capturedByName || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{l.notes || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-400">{l.location.lat.toFixed(5)}, {l.location.lng.toFixed(5)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const SeekFindDataPage: React.FC = () => (
  <GoogleMapProvider>
    <SeekFindDataInner />
  </GoogleMapProvider>
);

export default SeekFindDataPage;
